import { List, PlaylistAdd, Search as SearchIcon } from '@mui/icons-material';
import ReorderIcon from '@mui/icons-material/Reorder';
import { Box, Button, InputAdornment, Stack, TableCell, TableRow, TextField, Tooltip } from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2';
import { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import BayerTable, { type BayerTableColumn } from '/@/components/bayer/table';
import { TableTemplateCell } from '/@/components/bayer/table/template-cell';
import ChipLabel from '/@/components/chip-label';
import Icon from '/@/components/icon';
import RecommendationCommentsButton from '/@/components/recommendation-comments-button';
import SharePopup from '/@/components/share-popup';
import { useUnitProvider } from '/@/core/context/unit';
import { getCornPlantingDensity } from '/@/core/density';
import settings from '/@/core/settings';
import useDebounce from '/@/hooks/debounce/useDebounce';
import { useFieldById } from '/@/hooks/fields';
import { useProcessRecommendation } from '/@/hooks/my-recommendations/useFilterMyRecommendations';
import { useOwnUserNode } from '/@/hooks/nodes';
import { useRecommendationCommentsPermissions } from '/@/hooks/recommendation';
import { useHasWriteAccess, useValidatedType } from '/@/hooks/session';
import { _ } from '/@/locale';
import { MuiSelect } from '/@/mui/select';
import { emailRecommendationPDF, getRecommendationPDF } from '/@/store/private-data/recommendation-pdfs/actions';
import { useGetRecommendationsFromAPI } from '/@/store/private-data/recommendations/apiProviders';
import { getSessionBrands } from '/@/store/session/selectors';
import { mpTrackEvent } from '/@/store/tracking/mixpanel-events';
import type { Recommendation } from '/@/types/recommendation';
import { formatDate, getUserTypes } from '/@/utils';
import type { NodeScopeFilters } from '/@/utils/node-scope';
import { useCornDensityRecommendationQueries } from '/@/views/density-recommendation/detail';
import { FIELDS_EMPTY_STATE } from '../../growers/components/growers-list';
import { getCrops } from '../../hooks/crops';
import CreateRecommendationModal from '../../rv/components/create-recommendation-modal';
import RecommendationMoreOptions from './components/more-options';

export const useWhatIsRecommedation = (recommendation: Recommendation) => {
    const processRecommendation = useProcessRecommendation();
    const processedRecommendation = processRecommendation(recommendation);
    const units = useUnitProvider();
    const whatIsRecommedation = useMemo<{ title: string; subtitle?: string }>(() => {
        if (recommendation.cornDensity && recommendation.backup) {
            const cornDensityCurve = recommendation.backup?.otherData?.cornDensityCurve;
            const productId = recommendation.cornDensity?.productProfile;
            const productCornDensityCurve = cornDensityCurve?.[productId];
            const harvestDensity = productCornDensityCurve?.recommended?.density * 10000;
            const successRate = recommendation.backup.cornDensity?.userPlanterEfficiency;
            const seedsPerBag = recommendation.backup.otherData?.product?.seedsPerBag;

            if (!successRate || !seedsPerBag || !harvestDensity) {
                return {
                    title: '',
                };
            }

            const { density: plantingDensity, bagsPerHectare } = getCornPlantingDensity(
                harvestDensity,
                successRate,
                seedsPerBag
            );
            return {
                title: units.toCornDensity(plantingDensity, undefined, 'planting').toString(), // TODO: Conseguir densidad de siembra
                subtitle: _('Densidad de siembra'),
            };
        }
        return {
            title: processedRecommendation.product.join(' - '),
        };
    }, [recommendation, processedRecommendation, units]);

    return whatIsRecommedation;
};

const PageRecommendationRow = ({
    dataTestId,
    recommendation,
    setSelectedRecommendation,
    setDownloadPopupOpen,
    setSharePopupOpen,
    userNodeId,
}: {
    recommendation: Recommendation;
    setSelectedRecommendation: (row: Recommendation | null) => void;
    setDownloadPopupOpen: (open: boolean) => void;
    setSharePopupOpen: (open: boolean) => void;
    userNodeId: number;
    dataTestId?: string;
}) => {
    const userTypes = getUserTypes();
    const crops = getCrops();
    const { shouldShowCommentsButton } = useRecommendationCommentsPermissions(
        recommendation.commentsStatus,
        recommendation.userNode
    );
    const { data: ownNode } = useOwnUserNode();
    const isOwnUserNode = ownNode?.id === userNodeId;
    const processRecommendation = useProcessRecommendation();
    const processedRecommendation = processRecommendation(recommendation);
    const whatIsRecommedation = useWhatIsRecommedation(recommendation);
    const isShared = Boolean(recommendation.sharedStatus);

    const getTooltipText = () => {
        if (isShared) return _('Ya compartiste esta recomendación.');
        return _('Todavía no compartiste esta recomendación.');
    };

    const tooltipText = getTooltipText();

    return (
        <TableRow
            key={processedRecommendation.id}
            sx={{
                '&:last-child td, &:last-child th': { border: 0 },
                cursor: 'pointer',
                '&:hover': { backgroundColor: 'action.hover' },
            }}
            data-testid={dataTestId}
            onClick={() => {
                processedRecommendation.onClick(processedRecommendation);
            }}
        >
            <TableCell width='15%'>
                <ChipLabel
                    label={processedRecommendation.iconLabel}
                    type={processedRecommendation.type}
                    icon={<Icon type={processedRecommendation.icon} size='xs' theme={settings.app.theme} />}
                />
            </TableCell>
            <TableCell width='15%'>
                <TableTemplateCell title={processedRecommendation.fieldName} />
            </TableCell>
            <TableCell width='15%'>
                <TableTemplateCell title={formatDate(new Date(processedRecommendation.created))} />
            </TableCell>
            <TableCell width='10%'>
                <TableTemplateCell title={crops[processedRecommendation.crop]?.name} />
            </TableCell>
            <TableCell width='15%'>
                <TableTemplateCell title={whatIsRecommedation.title} subtitle={whatIsRecommedation.subtitle} />
            </TableCell>
            {!isOwnUserNode &&
                (() => {
                    const createdBy = recommendation.createdBy;
                    const isCreatedByCurrentUser = ownNode?.id === createdBy?.id;
                    const title = isCreatedByCurrentUser
                        ? _('Vos')
                        : createdBy
                          ? [createdBy.user?.firstName, createdBy.user?.lastName].filter(Boolean).join(' ') ||
                            _('sin datos')
                          : _('sin datos');
                    const subtitle =
                        !isCreatedByCurrentUser && createdBy
                            ? userTypes[createdBy.type as keyof typeof userTypes]
                            : undefined;
                    return (
                        <TableCell width='15%'>
                            <TableTemplateCell title={title} subtitle={subtitle} />
                        </TableCell>
                    );
                })()}
            <TableCell width='15%'>
                <Tooltip title={tooltipText} arrow>
                    <Box component='span' display='inline-block'>
                        <ChipLabel
                            label={isShared ? _('Compartido') : _('No compartido')}
                            type={isShared ? 'success' : 'danger'}
                        />
                    </Box>
                </Tooltip>
            </TableCell>
            <TableCell>
                <Stack direction='row' spacing={1} alignItems='center' justifyContent='flex-end'>
                    {shouldShowCommentsButton && (
                        <RecommendationCommentsButton
                            commentsStatus={recommendation.commentsStatus}
                            onOpenDrawer={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                processedRecommendation.onClick(processedRecommendation, { openComments: true });
                            }}
                            variant='list'
                        />
                    )}
                    <RecommendationMoreOptions
                        recommendation={recommendation}
                        onClickRedirect={() => processedRecommendation.onClick(processedRecommendation)}
                        downloadRecommendation={() => {
                            setSelectedRecommendation(recommendation);
                            setDownloadPopupOpen(true);
                        }}
                        shareRecommendation={() => {
                            setSelectedRecommendation(recommendation);
                            setSharePopupOpen(true);
                        }}
                    />
                </Stack>
            </TableCell>
        </TableRow>
    );
};

export const useMyRecommendationPDF = (selectedRecommendation?: Recommendation, entrySource?: string) => {
    const dispatch = useDispatch();
    const sessionBrands = useSelector(getSessionBrands);
    const processRecommendation = useProcessRecommendation();
    const {
        environmentsQuery,
        cornDensityCurveQuery,
        fertilizationCoefficientsQuery,
        yieldByEnvironmentQuery,
        productQuery,
        fieldQuery,
        recommendationQuery,
        growerQuery,
    } = useCornDensityRecommendationQueries({
        id: selectedRecommendation?.id,
        fieldId: selectedRecommendation?.field,
        productId: selectedRecommendation?.cornDensity?.productProfile,
        plantingDate: selectedRecommendation?.cornDensity?.plantingDate,
        soilMoisture: selectedRecommendation?.cornDensity?.soilMoisture,
        climatePattern: selectedRecommendation?.cornDensity?.climatePattern,
        environment: selectedRecommendation?.cornDensity?.environment,
        userDensity: selectedRecommendation?.cornDensity?.userDensity || 0,
    });

    if (!selectedRecommendation)
        return {
            handleEmail: () => {},
            handleWhatsApp: () => {},
            handleDownload: () => {},
        };

    const processedRecommendation = processRecommendation(selectedRecommendation);

    const brand = sessionBrands[selectedRecommendation?.crops?.[0]?.crop]?.[0];
    const cropRecoParsed = {
        ...processedRecommendation,
        ...selectedRecommendation,
        ...selectedRecommendation?.backup,
        otherData: {
            field: fieldQuery.data,
            product: productQuery.data,
            environments: environmentsQuery.data,
            cornDensityCurve: cornDensityCurveQuery.data,
            cornDensityCurveQueryIsLoading: cornDensityCurveQuery.isLoading,
            fertilizationCoefficients: fertilizationCoefficientsQuery.data,
            yieldByEnvironment: yieldByEnvironmentQuery.data,
            yieldByEnvironmentQueryIsLoading: yieldByEnvironmentQuery.isLoading,
            userNode: growerQuery.data,
            recommendation: recommendationQuery.data,
        },
        brand,
    };
    const cropProtectionRecoParsed = {
        ...processedRecommendation,
        ...selectedRecommendation?.backup,
        ...selectedRecommendation,
    };
    const parsedRecommendation =
        selectedRecommendation?.weedV2 ||
        selectedRecommendation?.diseaseRecommendation ||
        selectedRecommendation?.plagueRecommendation ||
        selectedRecommendation?.seedTreatmentRecommendation
            ? cropProtectionRecoParsed
            : cropRecoParsed;

    const handleEmail = async ({ email }) => {
        const file = await dispatch(
            emailRecommendationPDF({
                recommendation: parsedRecommendation,
                email,
            })
        );

        if (
            selectedRecommendation?.weedV2 ||
            selectedRecommendation?.diseaseRecommendation ||
            selectedRecommendation?.plagueRecommendation ||
            selectedRecommendation?.seedTreatmentRecommendation
        ) {
            mpTrackEvent('Crop Protection Recommendation shared', {
                source: entrySource || 'my-recommendations',
                crop: parsedRecommendation?.crop,
                cp_type: parsedRecommendation?.typeLabel,
                share_method: 'email',
            });
        } else if (selectedRecommendation?.cornDensity) {
            mpTrackEvent('Corn Density Recommendation shared', {
                share_method: 'email',
            });
        } else {
            mpTrackEvent('Seed recommendation shared', {
                share_method: 'email',
                crop: parsedRecommendation?.crop,
                number_of_hybrids: parsedRecommendation?.crops?.[0]?.productRecommendations?.length,
                entry_source: entrySource || 'my-recommendations',
            });
        }

        return {
            file,
            email,
        };
    };
    const handleWhatsApp = async () => {
        const file = await dispatch(
            getRecommendationPDF({
                recommendation: parsedRecommendation,
            })
        );

        // Track event
        if (
            selectedRecommendation?.weedV2 ||
            selectedRecommendation?.diseaseRecommendation ||
            selectedRecommendation?.plagueRecommendation ||
            selectedRecommendation?.seedTreatmentRecommendation
        ) {
            mpTrackEvent('Crop Protection Recommendation shared', {
                source: entrySource || 'my-recommendations',
                crop: parsedRecommendation?.crop,
                cp_type: parsedRecommendation?.typeLabel,
                share_method: 'whatsapp',
            });
        } else if (selectedRecommendation?.cornDensity) {
            mpTrackEvent('Corn Density Recommendation shared', {
                share_method: 'whatsapp',
            });
        } else {
            mpTrackEvent('Seed recommendation shared', {
                share_method: 'whatsapp',
                crop: parsedRecommendation?.crop,
                number_of_hybrids: parsedRecommendation?.crops?.[0]?.productRecommendations?.length,
                entry_source: entrySource || 'my-recommendations',
            });
        }

        return {
            file,
        };
    };
    const handleDownload = async () => {
        const file = await dispatch(
            getRecommendationPDF({
                recommendation: parsedRecommendation,
                download: true,
            })
        );

        // Track event

        if (
            selectedRecommendation?.weedV2 ||
            selectedRecommendation?.diseaseRecommendation ||
            selectedRecommendation?.plagueRecommendation ||
            selectedRecommendation?.seedTreatmentRecommendation
        ) {
            mpTrackEvent('Crop Protection Recommendation downloaded', {
                source: entrySource || 'my-recommendations',
                crop: parsedRecommendation?.crop,
                cp_type: parsedRecommendation?.typeLabel,
            });
        } else if (selectedRecommendation?.cornDensity) {
            mpTrackEvent('Corn Density Recommendation downloaded', undefined);
        } else {
            mpTrackEvent('Seed recommendation downloaded', {
                crop: parsedRecommendation?.crop,
                number_of_hybrids: parsedRecommendation?.crops?.[0]?.productRecommendations?.length,
                entry_source: entrySource || 'my-recommendations',
            });
        }

        return {
            file,
        };
    };

    return {
        handleEmail,
        handleWhatsApp,
        handleDownload,
    };
};

const RecommendationList = ({
    userNodeId,
    year,
    fieldId,
    nodeScope,
}: {
    userNodeId: number;
    year?: number;
    fieldId?: number;
    nodeScope: NodeScopeFilters;
}) => {
    // Table Management
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search, 1000);

    const [searchParams] = useSearchParams();
    const fieldIdFromQuery = Number(searchParams.get('fieldId')) || undefined;

    const [typeFilter, setTypeFilter] = useState('');
    const [fieldFilter, setFieldFilter] = useState<number | ''>(fieldId ?? fieldIdFromQuery ?? '');
    const [yearFilter, setYearFilter] = useState<number>(new Date().getFullYear());
    // order by
    const [orderBy, setOrderBy] = useState('field__name');
    const [order, setOrder] = useState<'asc' | 'desc'>('asc');

    // Table Queries
    const recommendationsListQuery = useGetRecommendationsFromAPI({
        page,
        pageSize,
        filters: {},
        ordering: orderBy ? `${order === 'asc' ? '' : '-'}${orderBy}` : undefined,
        node: userNodeId,
        year: year || yearFilter || undefined,
        fieldName: debouncedSearch || undefined,
        fieldId: fieldId || fieldFilter || undefined,
        type: typeFilter || undefined,
        ...nodeScope,
    });
    const { data: ownUserNode } = useOwnUserNode();

    const isOwnUserNode = ownUserNode?.id === userNodeId;

    const recommendations = useMemo(() => {
        return recommendationsListQuery.data?.results?.data || [];
    }, [recommendationsListQuery.data]);

    // Tabs Management
    const columns: BayerTableColumn[] = [
        { label: _('Tipo de recomendación'), value: 'type' },
        { label: _('Lote'), value: 'field__name', sortable: true, sortKey: 'field__name', sortOrder: 'asc' },
        { label: _('Fecha'), value: 'created', sortable: true, sortKey: 'created', sortOrder: 'asc' },
        { label: _('Cultivo'), value: 'crop' },
        { label: _('Recomendación'), value: 'recommendation' },
        ...(!isOwnUserNode ? [{ label: _('Creada por'), value: 'createdBy' }] : []),
        { label: _('Estado'), value: 'status' },
        { label: '', value: 'actions' },
    ];

    // PDF
    const [selectedRecommendation, setSelectedRecommendation] = useState<Recommendation | null>(null);
    const [downloadPopupOpen, setDownloadPopupOpen] = useState(false);
    const [sharePopupOpen, setSharePopupOpen] = useState(false);

    const fieldQuery = useFieldById({ id: fieldId });

    const { handleEmail, handleWhatsApp, handleDownload } = useMyRecommendationPDF(selectedRecommendation || undefined);
    const processRecommendation = useProcessRecommendation();
    const processedRecommendation = selectedRecommendation ? processRecommendation(selectedRecommendation) : null;
    const hasWriteAccess = useHasWriteAccess();
    const userType = useValidatedType();

    // Empty State
    const [newRecommendationModalOpen, setNewRecommendationModalOpen] = useState(false);
    const emptyState = useMemo(() => {
        let emptyIcon: React.ReactNode;
        let emptyTitle = '';
        let emptySubtitle = '';
        let emptyButton: React.ReactNode;

        const titles = FIELDS_EMPTY_STATE[userType as keyof typeof FIELDS_EMPTY_STATE] || FIELDS_EMPTY_STATE.default;

        const yearValue = year || yearFilter || undefined;

        emptyTitle = titles.recommendations.title;
        emptySubtitle = titles.recommendations.subtitle;

        if (!hasWriteAccess) {
            emptyButton = null;
        } else {
            if (yearValue === new Date().getFullYear()) {
                emptyButton = (
                    <Button variant='contained' color='secondary' onClick={() => setNewRecommendationModalOpen(true)}>
                        {_('Nueva recomendación')}
                    </Button>
                );
                if (hasWriteAccess) {
                    emptyIcon = <PlaylistAdd color='primary' sx={{ width: 32, height: 32 }} />;
                } else {
                    emptyIcon = <ReorderIcon color='primary' sx={{ width: 32, height: 32 }} />;
                }
            } else {
                emptyIcon = <List color='primary' sx={{ width: 32, height: 32 }} />;
                emptyTitle = _('No hay recomendaciones generadas este año.');
            }
        }

        if (search || typeFilter) {
            emptyTitle = _('No se encontraron resultados');
        }

        return {
            title: emptyTitle,
            subtitle: emptySubtitle,
            icon: emptyIcon,
            button: emptyButton,
        };
    }, [year, yearFilter, search, typeFilter, hasWriteAccess, userType]);

    return (
        <Stack spacing={3}>
            {/* Search and Filters */}
            <Grid2 container spacing={2} mx={'-8px !important'}>
                {!fieldId && (
                    <Grid2 xs={12} md>
                        <TextField
                            label={_('Buscar un lote por nombre')}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position='start'>
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                            }}
                            fullWidth
                        />
                    </Grid2>
                )}
                {!fieldId && (
                    <Grid2 xs={6} md={3}>
                        <MuiSelect
                            inputLabel={_('Lote')}
                            value={fieldFilter === '' ? '' : String(fieldFilter)}
                            onChange={(e) => setFieldFilter(e.target.value === '' ? '' : Number(e.target.value))}
                            data-testid='recommendations-list-field-filter'
                            options={
                                recommendationsListQuery.data?.results?.filterOptions?.field?.map((field) => ({
                                    value: String(field.value),
                                    label: field.label,
                                    dataTestId: `recommendations-list-field-filter-option-${field.value}`,
                                })) || []
                            }
                            formControlProps={{
                                fullWidth: true,
                            }}
                            clearable
                        />
                    </Grid2>
                )}
                <Grid2 xs={6} md={3}>
                    <MuiSelect
                        inputLabel={_('Tipo')}
                        value={typeFilter}
                        onChange={(e) => setTypeFilter(e.target.value)}
                        data-testid='recommendations-list-type-filter'
                        options={
                            recommendationsListQuery.data?.results?.filterOptions?.type?.map((type) => {
                                let label = _('Otro');
                                if (type === 'corn') {
                                    label = _('Híbrido de Maíz');
                                } else if (type === 'soy') {
                                    label = _('Híbrido de Soja');
                                } else if (type === 'corn_density') {
                                    label = _('Densidad de siembra');
                                } else if (type === 'weed') {
                                    label = _('Herbicida');
                                } else if (type === 'disease') {
                                    label = _('Fungicida');
                                } else if (type === 'plague') {
                                    label = _('Insecticida');
                                }
                                return {
                                    value: type,
                                    label,
                                    dataTestId: `recommendations-list-type-filter-option-${type}`,
                                };
                            }) || []
                        }
                        formControlProps={{
                            fullWidth: true,
                        }}
                        clearable
                    />
                </Grid2>
            </Grid2>
            {/* Table */}
            <BayerTable
                columns={columns}
                rows={recommendations.map((recommendation, index) => (
                    <PageRecommendationRow
                        dataTestId={`recommendation-row-${recommendation?.crops?.[0]?.crop || 'density'}`}
                        key={recommendation.id}
                        recommendation={recommendation}
                        setSelectedRecommendation={(row) => {
                            setSelectedRecommendation(row);
                        }}
                        setDownloadPopupOpen={setDownloadPopupOpen}
                        setSharePopupOpen={setSharePopupOpen}
                        userNodeId={userNodeId}
                    />
                ))}
                page={page}
                pageSize={pageSize}
                setPage={setPage}
                setPageSize={setPageSize}
                orderBy={orderBy}
                order={order}
                setOrderBy={setOrderBy}
                setOrder={setOrder}
                isValidating={recommendationsListQuery.isLoading}
                count={recommendationsListQuery.data?.count}
                emptyState={emptyState}
            />

            {selectedRecommendation && (
                <>
                    <SharePopup
                        type={processedRecommendation?.type}
                        title={_('Compartir tu recomendación')}
                        whatsAppIntro={_('Te envío una recomendación generada en {{ appName }}', {
                            appName: settings.app.name,
                        })}
                        visible={sharePopupOpen}
                        onClose={() => setSharePopupOpen(false)}
                        onEmail={handleEmail}
                        onWhatsApp={handleWhatsApp}
                        recommendation={selectedRecommendation}
                        entrySource='recommendation-list'
                        version={2}
                    />
                    <SharePopup
                        type={processedRecommendation?.type}
                        title={_('Descargar tu recomendación')}
                        visible={downloadPopupOpen}
                        onClose={() => setDownloadPopupOpen(false)}
                        onDownload={handleDownload}
                        recommendation={selectedRecommendation}
                    />
                </>
            )}
            <CreateRecommendationModal
                open={newRecommendationModalOpen}
                setOpen={setNewRecommendationModalOpen}
                userNodeId={userNodeId}
                selectedField={fieldQuery.data || undefined}
                setSelectedField={() => {}}
                entrySource='recommendation-list'
            />
        </Stack>
    );
};

export default RecommendationList;
